import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import { useAuth, useLoading, useSnackbar, useNavigation } from "../../Context/Contexts";
import { useTranslation } from "react-i18next";
import { CardVoucher } from "../../Types/Sales/CardVoucherTypes";
import { ListQuery } from "../../Types/ExportTypes";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { CustomerAutoComplete, CurrencyAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import LoadingSection from "../../components/LoadingSection";
import LocalizedNumberInputs from "../../components/Input/LocalizedNumberInputs"
import ResponsivePagination from "../../components/ResponsivePagination";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel"
import BlockIcon from "@mui/icons-material/Block";
import CardVoucherCancelDialog from "./CardVoucherCancelDialog";

import DefaultActions from "../../components/Card/DefaultActions";

import ScannerDialog from "../../components/Scanner/ScannerDialog";

const CardVoucherList: React.FC = () => {
  const [vouchers, setVouchers] = useState<CardVoucher[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [query, setQuery] = useCachedListQuery({
    page: 0,
    pageSize: 10,
    filter: "",
    filters: [],
  });

  const [filter, setFilter] = useState("");
  const [filterProperty, setFilterProperty] = useState("");
  const [loading, setLoading] = useState(false);
  const { authService } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const { showSnackbar } = useSnackbar();
  const { navigate } = useNavigation();
  const { t } = useTranslation();

  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const response = await authService.post<{ items: CardVoucher[]; count: number }>(
        "/cardVoucherApi/GetCardVouchers",
        query
      );
      setVouchers(response.items);
      setTotalCount(response.count);
    } catch (error: any) {
      showSnackbar(`${t("common.failedToFetchOrders")}: ${error.response?.data ?? error.message}`, "error");
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, [query]);

  useEffect(() => {
    if (loading) showLoading();
    else hideLoading();
  }, [loading, showLoading, hideLoading]);

  const isBooleanProperty = filterProperty === "ext_Active" || filterProperty === "ext_Cancelled";

  useEffect(() => {
    if (isBooleanProperty) setFilter("true");
  }, [isBooleanProperty]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    var localFilterProperty = filterProperty;
    //if (!filter) return;
    if (!localFilterProperty?.trim() && filter?.trim()) {
      localFilterProperty = "Default";
    }
    if (localFilterProperty?.trim() && (filter?.trim() || isBooleanProperty)) {
      const newFilter = buildQueryFilter(localFilterProperty, filter);
      const isDuplicate = query.filters?.some(
        (f) => f.property === newFilter.property && f.value === newFilter.value
      );
      if (!isDuplicate) {
        const newFilters = [...(query.filters ?? []), newFilter];
        setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
      }
    } else {
      setQuery({ ...query, page: 0, filter });
    }
    setFilter("");
    setFilterProperty("");
  };

  //const cancelVoucher = async (voucherCode: number) => {
  //  showLoading();
  //  try {
  //    await authService.post("/cardVoucherApi/Cancel", { code: voucherCode });
  //    fetchVouchers();
  //  } catch (error: any) {
  //    showSnackbar(`${t("common.failedToCancel")}: ${error.response?.data ?? error.message}`, "error");
  //  } finally {
  //    hideLoading();
  //  }
  //};

  const openVoucher = (voucherCode: number) => {
    navigate({ path: "/sales/cardvoucher/view", params: { code: voucherCode } });
  };

  const handlePageChange = (newPage: number) => {
    setQuery({ ...query, page: newPage });
  };


  const handleScannerOpen = useCallback(() => {
    setIsScannerOpen(true);
  }, []);

  const handleScannerClose = useCallback(() => {
    setIsScannerOpen(false);
  }, []);



  const scanBarcode = (barcode: string) => {
    const newFilter = buildQueryFilter("ext_Code", barcode);
    const newFilters = [...(query.filters ?? []), newFilter];
    setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
    return true;
  };


  const handleBarcodeScan = useCallback((code: string) => scanBarcode(code), [scanBarcode]);

  return (
    <CenteredListCard title={t("common.certificates")}>
      <ScannerDialog
        open={isScannerOpen}
        onClose={handleScannerClose}
        onScan={handleBarcodeScan}
      />

      <DefaultActions
        customActions={[
          {
            label: t("common.scanBarcode"),
            onClick: () => handleScannerOpen(),
          },
          {
            label: t("common.cancelCertificates"),
            onClick: () => setCancelDialogOpen(true),
          },
          {
            label: t("common.bonusBatch"),
            onClick: () => navigate({ path: "/sales/bonus/batch" }),
          }
        ]}
      />


      <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: "flex", gap: 2, mb: 2, mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={5} lg={2}>
            <TextField
              select
              label={t("common.field")}
              value={filterProperty}
              onChange={(e) => setFilterProperty(e.target.value)}
              SelectProps={{ native: true }}
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              <option value="">{t("common.default")}</option>
              <option value="ext_Code">{t("common.certificateCode")}</option>
              <option value="RemainingAmountGTE">{t("common.remainingAmountGTE")}</option>
              <option value="RemainingAmountLTE">{t("common.remainingAmountLTE")}</option>
              <option value="ext_CustomerNo">{t("common.customerNo")}</option>
              <option value="ext_CurrencyCode">{t("common.currencyCode")}</option>
              <option value="ext_Active">{t("common.active")}</option>
              <option value="ext_Cancelled">{t("common.redeemed")}</option>
              <option value="ext_ExternalCode">{t("common.externalCode")}</option>
            </TextField>
          </Grid>
          <Grid item xs={7} lg={7}>
            {(() => {
              switch (filterProperty) {
                case "ext_CustomerNo":
                  return (
                    <CustomerAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_CurrencyCode":
                  return (
                    <CurrencyAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_Active":
                  return (
                    <TextField
                      select
                      label={t("common.value")}
                      variant="outlined"
                      fullWidth
                      onChange={(e) => setFilter(e.target.value)}
                      value={filter}
                      disabled={loading}
                      SelectProps={{ native: true }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="true">{t("common.yes")}</option>
                      <option value="false">{t("common.no")}</option>
                    </TextField>

                  );
                case "ext_Cancelled":
                  return (
                    <TextField
                      select
                      label={t("common.value")}
                      variant="outlined"
                      fullWidth
                      onChange={(e) => setFilter(e.target.value)}
                      value={filter}
                      disabled={loading}
                      SelectProps={{ native: true }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="true">{t("common.yes")}</option>
                      <option value="false">{t("common.no")}</option>
                    </TextField>

                  )
                case "RemainingAmountGTE":
                  return (
                    <LocalizedNumberInputs
                      label={t("common.value")}
                      value={filter ? Number(filter) : null}
                      onValueChange={(value) => {
                        if (value) setFilter(value.toString());

                      }}
                      allowNegative={true} // Prevents negative numbers
                      decimalScale={4} // Ensures max 4 decimals
                      thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                      decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                    />

                  );
                case "RemainingAmountLTE":
                  return (
                    <LocalizedNumberInputs
                      label={t("common.value")}
                      value={filter ? Number(filter) : null}
                      onValueChange={(value) => {
                        if (value) setFilter(value.toString());

                      }}
                      allowNegative={true} // Prevents negative numbers
                      decimalScale={4} // Ensures max 4 decimals
                      thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                      decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                    />

                  );
                default:
                  return (
                    <TextField
                      label={t("common.search")}
                      variant="outlined"
                      fullWidth
                      onChange={(event) => setFilter(event.target.value)}
                      value={filter}
                      disabled={loading}
                    />
                  );
              }
            })()}
          </Grid>
          <Grid item xs={12} lg={3}>
            <Button variant="contained" type="submit" disabled={loading}>
              {t("common.search")}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {query.filters?.map((f, index) => (
          <Chip
            key={index}
            label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
            onDelete={() => {
              const newFilters = query.filters?.filter((_, i) => i !== index);
              setQuery({ ...query, filters: newFilters, page: 0 });
            }}
            deleteIcon={<DeleteIcon />}
          />
        ))}
      </Box>
      <LoadingSection loading={loading}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {vouchers.map((v) => (
            <Grid item xs={12} sm={6} md={4} key={v.code}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  transition: "0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                }}
              >
                <CardContent
                  onClick={() => openVoucher(v.code)}
                  sx={{
                    cursor: "pointer",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {/* Header */}
                  <ListCardHeader
                    icon={<CreditCardIcon fontSize="small" color="action" />}
                    title={v.code}
                    status={v.canceled
                      ? {
                        label: t("common.redeemed"),
                        color: "warning",
                        icon: <BlockIcon fontSize="small" />,
                      }
                      : v.active
                        ? {
                          label: t("common.active"),
                          color: "success",
                          icon: <CheckCircleIcon fontSize="small" />,
                        }
                        : {
                          label: t("common.inactive"),
                          color: "default",
                          icon: <CancelIcon fontSize="small" />,
                        }}
                  />

                  {/* Details */}
                  {v.externalCode && (
                    <Typography variant="body2" color="text.secondary">
                      {t("common.externalCode")}: {v.code}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {t("common.customerNo")}: {v.customerNo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("common.amount")}: {v.amount} {v.currencyCode}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("common.remainingAmount")}:   <strong> {v.remainingAmount} {v.currencyCode}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("common.created")}: {v.created ? new Date(v.created).toLocaleString() : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("common.validUntil")}: {v.validUntil ? new Date(v.validUntil).toLocaleString() : ""}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {vouchers.length === 0 && (
            <Grid item xs={12}>
              <Typography align="center">{t("common.noEntriesFound")}</Typography>
            </Grid>
          )}
        </Grid>
      </LoadingSection>
      <ResponsivePagination

        page={query.page}

        pageSize={query.pageSize}

        totalCount={totalCount}

        onPageChange={handlePageChange}

      />

      <CardVoucherCancelDialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} />

    </CenteredListCard>
  );
};

export default CardVoucherList;